// src/hooks/useSavedFilters.ts
import { useState, useCallback } from 'react';

export interface SavedFilter {
  id: string;
  name: string;
  filters: Record<string, string>;
  createdAt: string;
}

/**
 * useSavedFilters
 * Persist named filter sets to localStorage, keyed by pageId.
 *
 * Usage:
 *   const { saved, saveFilter, loadFilter, deleteFilter } = useSavedFilters('capa');
 */
export function useSavedFilters(pageId: string) {
  const storageKey = `qk_saved_filters_${pageId}`;

  const [saved, setSaved] = useState<SavedFilter[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch {
      return [];
    }
  });

  const persist = useCallback(
    (next: SavedFilter[]) => {
      setSaved(next);
      localStorage.setItem(storageKey, JSON.stringify(next));
    },
    [storageKey]
  );

  const saveFilter = useCallback(
    (name: string, filters: Record<string, string>) => {
      const newEntry: SavedFilter = {
        id: `${Date.now()}`,
        name,
        filters,
        createdAt: new Date().toISOString(),
      };
      persist([...saved, newEntry]);
    },
    [saved, persist]
  );

  const deleteFilter = useCallback(
    (id: string) => {
      persist(saved.filter((f) => f.id !== id));
    },
    [saved, persist]
  );

  const loadFilter = useCallback(
    (id: string): Record<string, string> | null => {
      const f = saved.find((s) => s.id === id);
      return f ? f.filters : null;
    },
    [saved]
  );

  return { saved, saveFilter, loadFilter, deleteFilter };
}
